import { useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';
import { addItemToCache, removeItemFromCache } from '@ts-core/core/utils/apolloUtils';

import { useAcademyProfessors } from 'modules/academies/hooks';
import { AcademyProfessor } from 'modules/academies/types';
import { useProfessorAuth } from 'modules/auth/hooks';

import {
  getCourseProfessorsQuery,
  GetCourseProfessorsQueryResult,
  GetCourseProfessorsQueryVariables,
} from '../graphql/queries/getCourseProfessorsQuery';
import {
  addProfessorToCourseMutation,
  AddProfessorToCourseMutationResult,
  AddProfessorToCourseMutationVariables,
} from '../graphql/mutations/addProfessorToCourseMutation';
import {
  removeProfessorFromCourseMutation,
  RemoveProfessorFromCourseMutationResult,
  RemoveProfessorFromCourseMutationVariables,
} from '../graphql/mutations/removeProfessorFromCourseMutation';

import { CourseProfessor } from '../types';

interface CourseProfessorsHookInput {
  courseId: string;
}

interface CourseProfessorsHookOutput {
  professors?: CourseProfessor[];
  availableAcademyProfessors?: AcademyProfessor[];
  loading: boolean;

  onAddProfessorCourse: (professor: AcademyProfessor, onSuccess: () => void) => void;
  onRemoveProfessorCourse: (professorId: string) => void;
}

export default function useCourseProfessors(input: CourseProfessorsHookInput): CourseProfessorsHookOutput {
  const { courseId } = input;
  const { showErrorNotification } = useSnackbarNotifications();
  const { data, loading } = useQuery<GetCourseProfessorsQueryResult, GetCourseProfessorsQueryVariables>(getCourseProfessorsQuery, {
    variables: { courseId },
    onError: showErrorNotification,
  });
  const courseProfessors = data?.courseProfessors;

  const { user } = useProfessorAuth();

  const { professors: academyProfessors } = useAcademyProfessors();
  const availableAcademyProfessors = useMemo(() => {
    return academyProfessors?.filter((professor) => {
      if (professor.id === user?._id) {
        return false;
      }
      return !courseProfessors?.find((courseProfessor) => courseProfessor.id === professor.id);
    });
  }, [academyProfessors, courseProfessors, user]);

  /** Add professor to course */
  const [addProfessorToCourse] = useMutation<AddProfessorToCourseMutationResult, AddProfessorToCourseMutationVariables>(
    addProfessorToCourseMutation,
    {
      onError: showErrorNotification,
      update: addItemToCache<AddProfessorToCourseMutationResult, GetCourseProfessorsQueryResult, GetCourseProfessorsQueryVariables>({
        query: getCourseProfessorsQuery,
        queryName: 'courseProfessors',
        queryVariables: { courseId },
        mutationName: 'courseProfessor',
      }),
    }
  );
  const onAddProfessorCourse = useCallback(
    (professor: AcademyProfessor, onSuccess: () => void) => {
      addProfessorToCourse({
        variables: { courseId, input: { professor: professor.id } },
        optimisticResponse: {
          courseProfessor: {
            id: professor.id,
            email: professor.email,
            name: professor.name,
            isOwner: false,
          },
        },
      }).then(onSuccess);
    },
    [addProfessorToCourse, courseId]
  );

  /** Remove professor from course */
  const [removeProfessorFromCourse] = useMutation<RemoveProfessorFromCourseMutationResult, RemoveProfessorFromCourseMutationVariables>(
    removeProfessorFromCourseMutation,
    {
      onError: showErrorNotification,
      update: removeItemFromCache<RemoveProfessorFromCourseMutationResult, GetCourseProfessorsQueryResult, GetCourseProfessorsQueryVariables>({
        query: getCourseProfessorsQuery,
        queryName: 'courseProfessors',
        queryVariables: { courseId },
        mutationName: 'courseProfessor',
        idAccessor: 'id',
      }),
    }
  );
  const onRemoveProfessorCourse = useCallback(
    (professorId: string) => {
      removeProfessorFromCourse({
        variables: { courseId, professorId },
        optimisticResponse: {
          courseProfessor: {
            id: professorId,
          },
        },
      });
    },
    [removeProfessorFromCourse, courseId]
  );

  return {
    professors: courseProfessors,
    loading,
    availableAcademyProfessors,

    onAddProfessorCourse,
    onRemoveProfessorCourse,
  };
}
